import { DollarSign, Star, Music, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminRevenueTab = () => {
  const { revenueData, refreshData } = useAdmin();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><DollarSign size={24} className="text-green-400" /> Revenue Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">Track subscriptions, bookings, and commissions</p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshData}><RefreshCw size={14} className="mr-2" />Refresh</Button>
      </div>

      {revenueData ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Revenue', value: revenueData.total_revenue, color: 'text-green-400', border: 'border-green-500/30' },
              { label: 'Subscriptions', value: revenueData.subscription_revenue, color: 'text-purple-400', border: 'border-purple-500/30', sub: `${revenueData.total_subscribers || 0} active` },
              { label: 'Booking Commissions', value: revenueData.commission_revenue, color: 'text-orange-400', border: 'border-orange-500/30', sub: `${revenueData.total_bookings || 0} bookings (3%)` },
              { label: 'Total Booking Value', value: revenueData.total_booking_value, color: 'text-blue-400', border: 'border-blue-500/30', sub: `${revenueData.pending_bookings || 0} pending` },
            ].map(({ label, value, color, border, sub }) => (
              <Card key={label} className={`bg-card ${border}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>${(value || 0).toFixed(2)}</p>
                  {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Subscribers by Tier */}
          <Card className="bg-card">
            <CardHeader><CardTitle className="text-base">Subscribers by Tier</CardTitle></CardHeader>
            <CardContent>
              {revenueData.subscribers_by_tier?.length > 0 ? (
                <div className="space-y-3">
                  {revenueData.subscribers_by_tier.map((tier) => (
                    <div key={tier.tier_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${tier.tier_id === 'featured' ? 'bg-yellow-500' : tier.tier_id === 'premium' ? 'bg-purple-500' : 'bg-emerald-500'}`} />
                        <span className="font-medium">{tier.tier_name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{tier.count}</span>
                        <span className="text-sm text-muted-foreground ml-2">(${(tier.revenue || 0).toFixed(2)})</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No subscribers yet. Share the pricing page to get started!</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Revenue Trend */}
          <Card className="bg-card">
            <CardHeader><CardTitle className="text-base">Monthly Revenue Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {revenueData.monthly_trend?.map((month) => {
                  const total = (month.subscriptions || 0) + (month.commissions || 0);
                  const maxRevenue = Math.max(...revenueData.monthly_trend.map(m => (m.subscriptions || 0) + (m.commissions || 0)), 1);
                  return (
                    <div key={month.month} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-16 shrink-0">{month.month}</span>
                      <div className="flex-1 flex gap-0.5 h-6 rounded overflow-hidden bg-muted/20">
                        {month.subscriptions > 0 && <div className="bg-purple-500/60 h-full" style={{ width: `${(month.subscriptions / maxRevenue) * 100}%` }} />}
                        {month.commissions > 0 && <div className="bg-orange-500/60 h-full" style={{ width: `${(month.commissions / maxRevenue) * 100}%` }} />}
                        {total === 0 && <div className="bg-muted/10 h-full w-full" />}
                      </div>
                      <span className="text-xs font-medium w-16 text-right">${total.toFixed(0)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-purple-500/60" /> Subscriptions</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-orange-500/60" /> Commissions</span>
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Star size={16} className="text-purple-400" /> Recent Subscriptions</CardTitle></CardHeader>
              <CardContent>
                {revenueData.recent_subscriptions?.length > 0 ? (
                  <div className="space-y-2">
                    {revenueData.recent_subscriptions.map((sub, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/20 text-sm">
                        <div><p className="font-medium">{sub.business_name}</p><p className="text-xs text-muted-foreground">{sub.tier_name}</p></div>
                        <span className="font-bold text-green-400">${(sub.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground text-center py-4">No subscriptions yet</p>}
              </CardContent>
            </Card>
            <Card className="bg-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Music size={16} className="text-orange-400" /> Recent Bookings</CardTitle></CardHeader>
              <CardContent>
                {revenueData.recent_bookings?.length > 0 ? (
                  <div className="space-y-2">
                    {revenueData.recent_bookings.map((b, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/20 text-sm">
                        <div><p className="font-medium">{b.artist_name}</p><p className="text-xs text-muted-foreground">{b.booker_name} - {b.event_venue}</p></div>
                        <div className="text-right"><p className="font-bold text-orange-400">${(b.commission || 0).toFixed(2)}</p><p className="text-[10px] text-muted-foreground">${(b.agreed_amount || 0).toFixed(0)} total</p></div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground text-center py-4">No bookings yet</p>}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card className="bg-card">
          <CardContent className="p-8 text-center">
            <DollarSign size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-bold">Revenue data loading...</h3>
            <p className="text-sm text-muted-foreground">Share the pricing page to start getting subscribers!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
